/**
 * The referral dashboard store — loads the referrer's code/stats/histories on
 * demand (the modal opens rarely). Thin: the edge is the sole authority on
 * qualification + payout math; this only holds the load state.
 */
import { type ApiError, isErr } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import type { ReferralSummaryView } from '../api/types.ts';

export interface ReferralStore {
  readonly summary: () => ReferralSummaryView | null;
  readonly loading: () => boolean;
  readonly error: () => ApiError | null;
  /** Fetch (or re-fetch) the summary. */
  load(): Promise<void>;
}

export function createReferralStore(api: ApiClient): ReferralStore {
  const [summary, setSummary] = createSignal<ReferralSummaryView | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getReferralSummary();
      if (isErr(result)) {
        setError(result.error);
        return;
      }
      setSummary(result.value);
    } finally {
      setLoading(false);
    }
  };

  return { summary, loading, error, load };
}
